import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import {
  CommunityAdminChannel,
  CommunityAdminChannelPayload,
  CommunityAdminGroup,
  CommunityAdminPermission,
  CommunityAdminRole,
  CommunityAdminStats,
  CommunityAdminStructure,
  CommunityAdminUser,
  CommunityAssignment,
  CommunityAssignmentListResponse,
  CommunityAssignmentPayload,
  CommunityCommunicationResponse,
  CommunityCommunicationRule,
  CommunityCommunicationRulePayload,
  CommunityContactGrant,
  CommunityContactGrantPayload,
  CommunityGroupPayload,
  CommunityPermissionFlags,
  CommunityPermissionTarget,
  CommunityPrivateRoomAccessLogResponse,
  CommunityPrivateRoomParticipant,
  CommunityPrivateRoomParticipantPayload,
  CommunityPrivateRoomParticipantsResponse,
  CommunityRolePayload,
  CommunityWebhookPayload,
  CommunityWebhookResponse,
  CommunityWebhooksResponse
} from '../models/community-admin.models';

export interface AssignmentListOptions {
  page?: number;
  perPage?: number;
  includeRevoked?: boolean;
  /** Free-text member filter (id, e-mail, name or username). */
  q?: string;
}

/**
 * Structure administration of the Community Network (`/api/community/admin/*`):
 * roles, grants, groups, channels and the rights matrix.
 *
 * The backend answers 403 (`community_forbidden`) to everyone who is neither an
 * admin nor an area admin, so callers treat that status as "not permitted", not
 * as a failure.
 */
@Injectable({ providedIn: 'root' })
export class CommunityAdminService {
  private readonly base = '/community/admin';
  private canManage$?: Observable<boolean>;

  constructor(private http: HttpClient) {}

  // --- Structure ------------------------------------------------------------

  getStructure(): Observable<CommunityAdminStructure> {
    return this.http.get<CommunityAdminStructure>(`${this.base}/structure`);
  }

  /**
   * Whether the current user may open the admin page at all — true for admins
   * and for area admins, who are not `user_type` admins and therefore cannot be
   * recognised from the auth state. Probes the structure endpoint once per
   * session; `resetCanManage()` drops the cached answer.
   */
  canManage(): Observable<boolean> {
    if (!this.canManage$) {
      this.canManage$ = this.getStructure().pipe(
        map(() => true),
        catchError(() => of(false)),
        shareReplay(1)
      );
    }
    return this.canManage$;
  }

  resetCanManage(): void {
    this.canManage$ = undefined;
  }

  // --- Roles ------------------------------------------------------------------

  createRole(role: CommunityRolePayload): Observable<{ role: CommunityAdminRole }> {
    return this.http.post<{ role: CommunityAdminRole }>(`${this.base}/roles`, { role });
  }

  updateRole(id: number, role: Partial<CommunityRolePayload>): Observable<{ role: CommunityAdminRole }> {
    return this.http.patch<{ role: CommunityAdminRole }>(`${this.base}/roles/${id}`, { role });
  }

  /** Deactivates the role and revokes every active grant with `reason`. */
  deleteRole(id: number, reason: string): Observable<{ ok: boolean }> {
    return this.http.delete<{ ok: boolean }>(`${this.base}/roles/${id}`, { body: { reason } });
  }

  // --- Grants -----------------------------------------------------------------

  listAssignments(roleId: number, options: AssignmentListOptions = {}): Observable<CommunityAssignmentListResponse> {
    let params = new HttpParams()
      .set('page', String(options.page ?? 1))
      .set('per_page', String(options.perPage ?? 25));
    if (options.includeRevoked) params = params.set('include_revoked', '1');
    if (options.q?.trim()) params = params.set('q', options.q.trim());
    return this.http.get<CommunityAssignmentListResponse>(`${this.base}/roles/${roleId}/assignments`, { params });
  }

  createAssignment(assignment: CommunityAssignmentPayload): Observable<{ assignment: CommunityAssignment; existing?: boolean }> {
    return this.http.post<{ assignment: CommunityAssignment; existing?: boolean }>(`${this.base}/assignments`, { assignment });
  }

  revokeAssignment(id: number, reason: string): Observable<{ assignment: CommunityAssignment }> {
    return this.http.delete<{ assignment: CommunityAssignment }>(`${this.base}/assignments/${id}`, { body: { reason } });
  }

  /** Admins only (area admins get 403). Matches id, e-mail, name or username; min. 2 characters. */
  searchUsers(q: string): Observable<{ data: CommunityAdminUser[] }> {
    return this.http.get<{ data: CommunityAdminUser[] }>(`${this.base}/users/search`, {
      params: new HttpParams().set('q', q)
    });
  }

  // --- Groups -----------------------------------------------------------------

  createGroup(group: CommunityGroupPayload): Observable<{ group: CommunityAdminGroup }> {
    return this.http.post<{ group: CommunityAdminGroup }>(`${this.base}/groups`, { group });
  }

  updateGroup(id: number, group: CommunityGroupPayload): Observable<{ group: CommunityAdminGroup }> {
    return this.http.patch<{ group: CommunityAdminGroup }>(`${this.base}/groups/${id}`, { group });
  }

  /** 422 `community_group_has_threads` while any of its channels still holds threads. */
  deleteGroup(id: number): Observable<{ ok: boolean }> {
    return this.http.delete<{ ok: boolean }>(`${this.base}/groups/${id}`);
  }

  reorderGroups(ids: number[]): Observable<{ ok: boolean }> {
    return this.http.post<{ ok: boolean }>(`${this.base}/groups/reorder`, { ids });
  }

  // --- Channels ---------------------------------------------------------------

  createChannel(channel: CommunityAdminChannelPayload): Observable<{ channel: CommunityAdminChannel }> {
    return this.http.post<{ channel: CommunityAdminChannel }>(`${this.base}/channels`, { channel });
  }

  updateChannel(id: number, channel: CommunityAdminChannelPayload): Observable<{ channel: CommunityAdminChannel }> {
    return this.http.patch<{ channel: CommunityAdminChannel }>(`${this.base}/channels/${id}`, { channel });
  }

  /** With threads inside, the backend refuses (422) unless `moveTo` names the channel that takes them. */
  deleteChannel(id: number, moveTo?: number | null): Observable<{ ok: boolean }> {
    let params = new HttpParams();
    if (moveTo != null) params = params.set('move_to', String(moveTo));
    return this.http.delete<{ ok: boolean }>(`${this.base}/channels/${id}`, { params });
  }

  reorderChannels(groupId: number, ids: number[]): Observable<{ ok: boolean }> {
    return this.http.post<{ ok: boolean }>(`${this.base}/channels/reorder`, { group_id: groupId, ids });
  }

  // --- Private Rooms ------------------------------------------------------------
  // Turning a channel private and the participants' read-only mode go through
  // `updateChannel` (`private: true` / `participants_read_only`).

  listParticipants(channelId: number): Observable<CommunityPrivateRoomParticipantsResponse> {
    return this.http.get<CommunityPrivateRoomParticipantsResponse>(`${this.base}/channels/${channelId}/participants`);
  }

  /** 409 `already_participant`; 422 `not_private` or `{ errors }`. */
  addParticipant(channelId: number, participant: CommunityPrivateRoomParticipantPayload): Observable<{ participant: CommunityPrivateRoomParticipant }> {
    return this.http.post<{ participant: CommunityPrivateRoomParticipant }>(`${this.base}/channels/${channelId}/participants`, { participant });
  }

  /** Revokes the participant's assignment; the reason is mandatory. */
  removeParticipant(channelId: number, assignmentId: number, reason: string): Observable<{ participant: CommunityPrivateRoomParticipant }> {
    return this.http.delete<{ participant: CommunityPrivateRoomParticipant }>(
      `${this.base}/channels/${channelId}/participants/${assignmentId}`,
      { body: { reason } }
    );
  }

  /** Accesses by platform admins who are neither participant nor area admin of the room. */
  accessLog(channelId: number, page = 1, perPage = 20): Observable<CommunityPrivateRoomAccessLogResponse> {
    const params = new HttpParams().set('page', String(page)).set('per_page', String(perPage));
    return this.http.get<CommunityPrivateRoomAccessLogResponse>(`${this.base}/channels/${channelId}/access_log`, { params });
  }

  // --- Rights matrix ----------------------------------------------------------

  /** Replaces the row completely: every flag not sent becomes false. */
  savePermission(target: CommunityPermissionTarget, flags: CommunityPermissionFlags): Observable<{ permission: CommunityAdminPermission }> {
    return this.http.put<{ permission: CommunityAdminPermission }>(`${this.base}/permissions`, { ...target, flags });
  }

  deletePermission(target: CommunityPermissionTarget): Observable<{ ok: boolean }> {
    let params = new HttpParams().set('role_id', String(target.role_id));
    if (target.channel_id != null) params = params.set('channel_id', String(target.channel_id));
    else if (target.group_id != null) params = params.set('group_id', String(target.group_id));
    return this.http.delete<{ ok: boolean }>(`${this.base}/permissions`, { params });
  }

  // --- Communication rules & contact grants (admins / manage_roles) ----------

  getCommunication(): Observable<CommunityCommunicationResponse> {
    return this.http.get<CommunityCommunicationResponse>(`${this.base}/communication`);
  }

  createRule(rule: CommunityCommunicationRulePayload): Observable<{ rule: CommunityCommunicationRule }> {
    return this.http.post<{ rule: CommunityCommunicationRule }>(`${this.base}/communication_rules`, { rule });
  }

  updateRule(id: number, rule: CommunityCommunicationRulePayload): Observable<{ rule: CommunityCommunicationRule }> {
    return this.http.patch<{ rule: CommunityCommunicationRule }>(`${this.base}/communication_rules/${id}`, { rule });
  }

  deleteRule(id: number): Observable<{ ok: boolean }> {
    return this.http.delete<{ ok: boolean }>(`${this.base}/communication_rules/${id}`);
  }

  /** Returns one grant, or two with `both_ways`; an existing active pair is reused, not duplicated. */
  createContactGrant(grant: CommunityContactGrantPayload): Observable<{ grants: CommunityContactGrant[] }> {
    return this.http.post<{ grants: CommunityContactGrant[] }>(`${this.base}/contact_grants`, { grant });
  }

  revokeContactGrant(id: number): Observable<{ grant: CommunityContactGrant }> {
    return this.http.delete<{ grant: CommunityContactGrant }>(`${this.base}/contact_grants/${id}`);
  }

  // --- Statistics (Phase 5) -----------------------------------------------------

  getStats(days = 30): Observable<CommunityAdminStats> {
    return this.http.get<CommunityAdminStats>(`${this.base}/stats`, { params: new HttpParams().set('days', String(days)) });
  }

  // --- Webhooks (Phase 5, `can_manage_roles` only) --------------------------------

  getWebhooks(): Observable<CommunityWebhooksResponse> {
    return this.http.get<CommunityWebhooksResponse>(`${this.base}/webhooks`);
  }

  /** 201 with `secret` present — shown to the admin exactly once. */
  createWebhook(webhook: CommunityWebhookPayload): Observable<CommunityWebhookResponse> {
    return this.http.post<CommunityWebhookResponse>(`${this.base}/webhooks`, { webhook });
  }

  /** `secret` is present in the response only when `webhook.rotate_secret` was sent. */
  updateWebhook(id: number, webhook: Partial<CommunityWebhookPayload>): Observable<CommunityWebhookResponse> {
    return this.http.patch<CommunityWebhookResponse>(`${this.base}/webhooks/${id}`, { webhook });
  }

  deleteWebhook(id: number): Observable<{ ok: boolean }> {
    return this.http.delete<{ ok: boolean }>(`${this.base}/webhooks/${id}`);
  }

  testWebhook(id: number): Observable<{ ok: boolean }> {
    return this.http.post<{ ok: boolean }>(`${this.base}/webhooks/${id}/test`, {});
  }
}
